import React, { useMemo } from "react";
import { Form, Segment, Label, Grid } from "semantic-ui-react";
import { useFormik } from "formik";
import * as Icons from "react-icons/fa";
import { Subject } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { initialValues, validationSchema } from "./SubjectForm.form";
import "./SubjectForm.scss";

const subjectController = new Subject();

// Build web icon options from react-icons/fa (only Fa* components)
const webIconOptions = Object.keys(Icons)
  .filter((key) => key.startsWith("Fa") && typeof Icons[key] === "function")
  .map((key) => ({ key, value: key, text: key }));

// Mobile icons (Flutter font_awesome_flutter) with their FA web equivalent for preview
const MOBILE_ICONS = [
  { value: "handsPraying", label: "Manos orando", faIcon: "FaPray" },
  { value: "flask", label: "Frasco (ciencias)", faIcon: "FaFlask" },
  { value: "globe", label: "Globo terráqueo", faIcon: "FaGlobe" },
  { value: "language", label: "Idiomas", faIcon: "FaLanguage" },
  { value: "tools", label: "Herramientas", faIcon: "FaTools" },
  { value: "calculator", label: "Calculadora", faIcon: "FaCalculator" },
  { value: "comments", label: "Comunicación", faIcon: "FaComments" },
  { value: "personRunning", label: "Persona corriendo", faIcon: "FaRunning" },
  { value: "palette", label: "Arte y cultura", faIcon: "FaPalette" },
  { value: "users", label: "Ciudadanía", faIcon: "FaUsers" },
  { value: "book", label: "Libro", faIcon: "FaBook" },
  { value: "graduationCap", label: "Educación", faIcon: "FaGraduationCap" },
  { value: "chalkboard", label: "Pizarrón", faIcon: "FaChalkboard" },
  { value: "microscope", label: "Microscopio", faIcon: "FaMicroscope" },
  { value: "music", label: "Música", faIcon: "FaMusic" },
  { value: "paintBrush", label: "Pincel", faIcon: "FaPaintBrush" },
  { value: "dumbbell", label: "Educación física", faIcon: "FaDumbbell" },
  { value: "brain", label: "Inteligencia", faIcon: "FaBrain" },
  { value: "pencilAlt", label: "Escritura", faIcon: "FaPencilAlt" },
  { value: "atom", label: "Física / Química", faIcon: "FaAtom" },
  { value: "seedling", label: "Medio ambiente", faIcon: "FaSeedling" },
  { value: "coins", label: "Economía", faIcon: "FaCoins" },
  { value: "landmark", label: "Historia", faIcon: "FaLandmark" },
  { value: "chess", label: "Ajedrez / Lógica", faIcon: "FaChess" },
  { value: "trophy", label: "Logros", faIcon: "FaTrophy" },
  { value: "futbol", label: "Fútbol", faIcon: "FaFutbol" },
  { value: "star", label: "Destacado", faIcon: "FaStar" },
  { value: "lightbulb", label: "Innovación", faIcon: "FaLightbulb" },
  { value: "leaf", label: "Ecología", faIcon: "FaLeaf" },
  { value: "dna", label: "Biología", faIcon: "FaDna" },
];

const mobileIconOptions = MOBILE_ICONS.map(({ value, label }) => ({
  key: value,
  value,
  text: `${value} — ${label}`,
}));

export function SubjectForm(props) {
  const { onClose, onReload, subject } = props;
  const { accessToken } = useAuth();

  const formik = useFormik({
    initialValues: initialValues(subject),
    validationSchema: validationSchema(),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      try {
        const data = {
          name: formValue.name,
          icon: formValue.icon,
          mobileIcon: formValue.mobileIcon,
        };

        if (subject) {
          await subjectController.updateSubject(accessToken, subject.id, data);
        } else {
          data.status = 1;
          await subjectController.createSubject(accessToken, data);
        }
        onReload();
        onClose();
      } catch (error) {
        console.error(error);
      }
    },
  });

  const SelectedWebIcon = useMemo(
    () => (formik.values.icon ? Icons[formik.values.icon] : null),
    [formik.values.icon]
  );

  const selectedMobile = useMemo(
    () => MOBILE_ICONS.find((m) => m.value === formik.values.mobileIcon),
    [formik.values.mobileIcon]
  );

  const MobilePreviewIcon = useMemo(
    () => (selectedMobile ? Icons[selectedMobile.faIcon] || null : null),
    [selectedMobile]
  );

  return (
    <Form onSubmit={formik.handleSubmit}>
      <Form.Input
        name="name"
        label="Nombre de asignatura"
        onChange={formik.handleChange}
        value={formik.values.name}
        error={formik.errors.name}
      />

      {/* Ícono web */}
      <Grid columns={2} stackable>
        <Grid.Column>
          <Form.Select
            className="edunova-select"
            label="Ícono web (react-icons/fa)"
            placeholder="Busca y selecciona un ícono"
            options={webIconOptions}
            value={formik.values.icon}
            onChange={(_, { value }) => formik.setFieldValue("icon", value)}
            search
            clearable
            error={formik.errors.icon}
          />
        </Grid.Column>
        <Grid.Column>
          <div style={{ paddingTop: "1.75rem" }}>
            {SelectedWebIcon ? (
              <Segment
                textAlign="center"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "0.5rem",
                  padding: "1rem",
                }}
              >
                <div style={{ fontSize: "2.5rem", lineHeight: 1 }}>
                  <SelectedWebIcon />
                </div>
                <Label size="tiny" color="blue">
                  {formik.values.icon}
                </Label>
              </Segment>
            ) : (
              <Segment
                textAlign="center"
                secondary
                style={{ color: "#aaa", padding: "1rem" }}
              >
                Vista previa
              </Segment>
            )}
          </div>
        </Grid.Column>
      </Grid>

      {/* Ícono móvil */}
      <Grid columns={2} stackable style={{ marginTop: 0 }}>
        <Grid.Column>
          <Form.Select
            className="edunova-select"
            label="Ícono móvil (Flutter)"
            placeholder="Busca y selecciona un ícono"
            options={mobileIconOptions}
            value={formik.values.mobileIcon}
            onChange={(_, { value }) =>
              formik.setFieldValue("mobileIcon", value)
            }
            search
            clearable
            error={formik.errors.mobileIcon}
          />
        </Grid.Column>
        <Grid.Column>
          <div style={{ paddingTop: "1.75rem" }}>
            {selectedMobile ? (
              <Segment
                textAlign="center"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "0.5rem",
                  padding: "1rem",
                }}
              >
                {MobilePreviewIcon && (
                  <div style={{ fontSize: "2.5rem", lineHeight: 1 }}>
                    <MobilePreviewIcon />
                  </div>
                )}
                <Label size="tiny" color="teal">
                  {selectedMobile.value}
                </Label>
                <span style={{ fontSize: "0.75rem", color: "#888" }}>
                  {selectedMobile.label}
                </span>
              </Segment>
            ) : (
              <Segment
                textAlign="center"
                secondary
                style={{ color: "#aaa", padding: "1rem" }}
              >
                Vista previa
              </Segment>
            )}
          </div>
        </Grid.Column>
      </Grid>

      <Form.Button
        type="submit"
        primary
        fluid
        loading={formik.isSubmitting}
        className="subject-form__submit"
      >
        {subject ? "Actualizar asignatura" : "Crear asignatura"}
      </Form.Button>
    </Form>
  );
}